import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { SidebarItem } from "../../layouts/sideBarItems";
import { motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBars, faArrowLeft } from "@fortawesome/free-solid-svg-icons";

interface SidebarProps {
  items: SidebarItem[];
  logo?: string;
  appName?: string;
  userName?: string;
  userAvatar?: string;
}

const Sidebar: React.FC<SidebarProps> = ({
  items,
  logo,
  appName = "Team-Pulse",
  userName,
  userAvatar
}) => {
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(false);

  const [filteredItems, setFilteredItems] = useState<SidebarItem[]>(items);

  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setIsCollapsed(true);
      }
    };

    handleResize(); // Call initially
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Filter menu items based on search
  useEffect(() => {
    // Since search is disabled, just use all items
    setFilteredItems(items);


  }, [items]);

  // Sidebar animation variants
  const sidebarVariants = {
    expanded: { width: "240px", transition: { duration: 0.3 } },
    collapsed: { width: "70px", transition: { duration: 0.3 } }
  };

  // Item animation variants
  const itemVariants = {
    expanded: { opacity: 1, x: 0, transition: { duration: 0.3 } },
    collapsed: { opacity: 0, x: -10, transition: { duration: 0.3 } }
  };

  return (
    <motion.div
      className="h-screen bg-white dark:bg-gray-800 shadow-lg overflow-hidden relative"
      initial="expanded"
      animate={isCollapsed ? "collapsed" : "expanded"}
      variants={sidebarVariants}
    >
      {/* App Header */}
      <div className="flex items-center justify-between h-16 px-4 shadow-sm">
        {!isCollapsed && (
          <motion.div
            className="flex items-center gap-2"
            variants={itemVariants}
          >
            <div className="w-8 h-8">
              {logo && <img src={logo} alt="Logo" className="w-full h-full object-contain" />}
            </div>
            <span className="text-lg font-semibold text-gray-800 dark:text-white">{appName}</span>
          </motion.div>
        )}

        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300"
        >
          <FontAwesomeIcon icon={isCollapsed ? faBars : faArrowLeft} />
        </button>
      </div>



      {/* Menu Items */}
      <div className="mt-4 px-2 overflow-y-auto h-[calc(100vh-180px)]">
        {filteredItems.map((item, index) => {
          const isActive = location.pathname === item.url;

          return (
            <Link
              to={item.url}
              key={item.name}
              className={`flex items-center py-3 px-3 my-1 rounded-lg transition-all duration-200 ${isActive
                ? "bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-300"
                : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                }`}
            >
              <div className={`${isActive ? "text-blue-600 dark:text-blue-300" : "text-gray-500 dark:text-gray-400"} text-xl`}>
                <item.icon />
              </div>

              {!isCollapsed && (
                <motion.span
                  variants={itemVariants}
                  className="ml-3 font-medium overflow-hidden whitespace-nowrap"
                >
                  {item.name}
                </motion.span>
              )}
            </Link>
          );
        })}
      </div>

      {/* User Profile Section - Only visible when expanded */}
      <AnimatePresence>
        {!isCollapsed && userName && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="absolute bottom-0 left-0 right-0 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 p-3"
          >
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center overflow-hidden">
                {userAvatar ? (
                  <img src={userAvatar} alt={userName} className="w-full h-full object-cover" />
                ) : (
                  <span className="text-white text-sm font-bold">
                    {userName.substring(0, 1).toUpperCase()}
                  </span>
                )}
              </div>
              <div className="flex-1 overflow-hidden">
                <p className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">
                  {userName}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-200 truncate">
                  version:1.0.1
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default Sidebar;
